import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Radio, FileText, Search, Lock, DollarSign, Gavel, Folder, ChevronRight, ChevronLeft, Loader2, ClipboardCheck, TrendingUp, ShieldAlert, ArrowLeft, Calendar, Clock, MapPin, Scale, MessageSquareQuote } from "lucide-react";
import AssessmentQuiz from "../components/AssessmentQuiz";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { RADIO_CODES, STATUS_CODES } from "../data/codes";
import { useAuth } from "../context/AuthContext";
import { ExamPanel } from "./AreaAllievi";
import ManualeOperatore from "../components/ManualeOperatore";
import { ExamAuthRequestPanel } from "../components/ExamAuthRequestPanel";
import MandatiPanel from "../components/MandatiPanel";
import FeedbackPanel from "../components/FeedbackPanel";

// Ruoli che hanno un esame di promozione disponibile in Area Agenti
const PROMOTION_EXAM_ROLES = new Set(["Vice Sceriffo II", "Sergente", "Tenente"]);
// Ruoli che vedono l'esame da Sergente ma in modalità bloccata (devono raggiungere Vice Sceriffo II)
const LOCKED_EXAM_ROLES = new Set(["Vice Sceriffo", "Vice Sceriffo I"]);

const AGENTI_VIEWS = { HOME: "home", PENAL: "penal", RADIO: "radio", SOP: "sop", EXAM: "exam", CALENDAR: "calendar", MANDATI: "mandati", ASSESSMENT: "assessment", FEEDBACK: "feedback" };

const SEVERITY = {
  "INFRAZIONE": { chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", dot: "bg-emerald-400", order: 1 },
  "REATO MINORE": { chip: "bg-amber-500/15 text-amber-300 border-amber-500/40", dot: "bg-amber-400", order: 2 },
  "REATO MAGGIORE": { chip: "bg-rose-600/20 text-rose-300 border-rose-600/50", dot: "bg-rose-400", order: 3 },
};
const CATEGORY_ORDER = [
  "EMENDAMENTI", "DISPOSIZIONI", "REATI CONTRO LA PERSONA", "CRIMINI CONTRO LA PROPRIETÀ",
  "REATI CONTRO ORDINE PUBBLICO", "REATI CONTRO LA PUBBLICA AMMINISTRAZIONE",
  "REATI CONTRO L'AMMINISTRAZIONE DELLA GIUSTIZIA", "REATI LEGATI ALLE ARMI",
  "REATI LEGATI ALLE SOSTANZE STUPEFACENTI", "REATI FINANZIARI ED ECONOMICI",
  "REATI CONTRO LA SICUREZZA PUBBLICA", "REATI CONTRO LA FAMIGLIA E I MINORI", "CODICE DELLA STRADA",
];

export default function AreaAgenti() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(AGENTI_VIEWS.HOME);
  const goHome = useCallback(() => {
    setView(AGENTI_VIEWS.HOME);
    // Rimuove il query param quando torniamo alla home per non lasciare l'URL "sporco"
    if (searchParams.get("view")) {
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const canTakeExam = user && PROMOTION_EXAM_ROLES.has(user.role);
  const isLockedExam = user && LOCKED_EXAM_ROLES.has(user.role);
  const showExam = canTakeExam || isLockedExam;

  // Deep-link: `?view=feedback` (o altre view) apre direttamente la sezione.
  // Usato dal promemoria Discord del Comando delle 20:00 per portare l'operatore
  // dritto al pannello Feedback con un click.
  useEffect(() => {
    const initial = (searchParams.get("view") || "").toLowerCase();
    const valid = Object.values(AGENTI_VIEWS);
    if (initial && valid.includes(initial)) {
      setView(initial);
    }
    // eseguito solo al primo mount / o quando cambia il param
  }, [searchParams]);

  if (view === AGENTI_VIEWS.HOME) {
    return (
      <AgentiHomePanels
        showExam={showExam}
        isLockedExam={isLockedExam}
        userRole={user?.role}
        onOpenPenal={() => setView(AGENTI_VIEWS.PENAL)}
        onOpenRadio={() => setView(AGENTI_VIEWS.RADIO)}
        onOpenSop={() => setView(AGENTI_VIEWS.SOP)}
        onOpenAssessment={() => setView(AGENTI_VIEWS.ASSESSMENT)}
        onOpenExam={() => setView(AGENTI_VIEWS.EXAM)}
        onOpenCalendar={() => setView(AGENTI_VIEWS.CALENDAR)}
        onOpenMandati={() => setView(AGENTI_VIEWS.MANDATI)}
        onOpenFeedback={() => setView(AGENTI_VIEWS.FEEDBACK)}
      />
    );
  }
  if (view === AGENTI_VIEWS.PENAL) return <AgentiBackWrap onBack={goHome} title="Codice Penale" icon={FileText}><PenalCodeBrowser /></AgentiBackWrap>;
  if (view === AGENTI_VIEWS.RADIO) return <AgentiBackWrap onBack={goHome} title="Codici Radio" icon={Radio}><RadioCodes /></AgentiBackWrap>;
  if (view === AGENTI_VIEWS.CALENDAR) return <AgentiBackWrap onBack={goHome} title="Calendario Eventi" icon={Calendar}><AgentiCalendarView /></AgentiBackWrap>;
  if (view === AGENTI_VIEWS.MANDATI) return <AgentiBackWrap onBack={goHome} title="Richieste Mandato Procura" icon={Scale}><MandatiPanel /></AgentiBackWrap>;
  if (view === AGENTI_VIEWS.FEEDBACK) return <AgentiBackWrap onBack={goHome} title="Feedback Operatori" icon={MessageSquareQuote}><FeedbackPanel /></AgentiBackWrap>;
  if (view === AGENTI_VIEWS.SOP) {
    return (
      <AgentiBackWrap onBack={goHome} title="Manuale dell'Operatore" icon={BookOpen}>
        <ManualeOperatore />
      </AgentiBackWrap>
    );
  }
  if (view === AGENTI_VIEWS.EXAM && showExam) {
    return (
      <AgentiBackWrap onBack={goHome} title="Esame di Promozione" icon={TrendingUp}>
        {isLockedExam
          ? <LockedPromotionExam userRole={user.role} requiredRole="Vice Sceriffo II" nextRole="Sergente" />
          : <PromotionExamLauncher userRole={user.role} />}
      </AgentiBackWrap>
    );
  }
  if (view === AGENTI_VIEWS.ASSESSMENT) {
    return (
      <AgentiBackWrap onBack={goHome} title="Quiz Valutazione Grado" icon={ClipboardCheck}>
        <AssessmentQuiz />
      </AgentiBackWrap>
    );
  }
  return null;
}

function AgentiBackWrap({ onBack, title, icon: Icon, children }) {
  return (
    <div className="space-y-6" data-testid="area-agenti">
      <div className="flex items-center gap-3">
        <Button
          onClick={onBack}
          variant="outline"
          size="sm"
          data-testid="back-to-agenti-home"
          className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
        </Button>
        <div className="flex items-center gap-2 text-sheriff-gold">
          <Icon className="h-5 w-5" />
          <span className="font-heading font-bold text-xl text-[#F5E6B8]">{title}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

function AgentiHomePanels({ showExam, isLockedExam, userRole, onOpenPenal, onOpenRadio, onOpenSop, onOpenExam, onOpenCalendar, onOpenMandati, onOpenAssessment, onOpenFeedback }) {
  const [examBadge, setExamBadge] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);

  useEffect(() => {
    api.get("/events", { params: { upcoming: true, limit: 1 } })
      .then(({ data }) => setNextEvent(data?.[0] || null))
      .catch(() => setNextEvent(null));
  }, []);

  useEffect(() => {
    if (!showExam || isLockedExam) return;
    api.get("/quiz/questions").then(({ data }) => {
      if (data?.session_open && !data?.rate_limited) setExamBadge("disponibile");
      else if (data?.session_open && data?.block_reason === "cooldown") setExamBadge("cooldown");
      else setExamBadge(null);
    }).catch(() => setExamBadge(null));
  }, [showExam, isLockedExam, userRole]);

  let examBadgeProps = null;
  if (isLockedExam) {
    examBadgeProps = { badge: "—", badgeLabel: "bloccato", badgeTone: "amber" };
  } else if (examBadge === "disponibile") {
    examBadgeProps = { badge: "•", badgeLabel: "bando aperto", badgeTone: "gold" };
  } else if (examBadge === "cooldown") {
    examBadgeProps = { badge: "•", badgeLabel: "cooldown 5gg", badgeTone: "amber" };
  }

  return (
    <div className="space-y-8" data-testid="area-agenti">
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <BookOpen className="h-4 w-4" /> Area Agenti
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8] mb-2">
          Documentazione Operativa
        </h1>
        <p className="text-[#D4C480] max-w-2xl">
          Consulta il Codice Penale, i Codici Radio, il Manuale dell&apos;Operatore e — se idoneo —
          sostieni l&apos;esame di promozione al grado successivo.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <AgentiPanelCard
          icon={FileText}
          title="Codice Penale"
          subtitle="Consultazione sola lettura"
          desc="Sfoglia tutti gli articoli in vigore organizzati per capitoli, ricerca per codice o reato, consulta sanzioni e procedure giudiziarie."
          ctaLabel="Apri Codice"
          onClick={onOpenPenal}
          testId="agenti-panel-penal"
        />
        <AgentiPanelCard
          icon={Radio}
          title="Codici Radio"
          subtitle="Comunicazioni operative"
          desc="Riferimento rapido per i codici 10 di emergenza, codici operativi e stato veicolo. Ricerca istantanea per codice o descrizione."
          ctaLabel="Apri Radio"
          onClick={onOpenRadio}
          testId="agenti-panel-radio"
        />
        <AgentiPanelCard
          icon={BookOpen}
          title="Manuale Operatore"
          subtitle="Storia, struttura, procedure"
          desc="Manuale ufficiale del Dipartimento: storia LSSD, gerarchia dei gradi, divisioni operative e logistiche, e tutte le procedure standard."
          ctaLabel="Apri Manuale"
          onClick={onOpenSop}
          testId="agenti-panel-sop"
        />
        <AgentiPanelCard
          icon={ClipboardCheck}
          title="Quiz Valutazione Grado"
          subtitle="Test di autovalutazione"
          desc="Rispondi a 40 domande estratte dal Manuale dell'Operatore. Serve autorizzazione HR per iniziare; al termine il Comando valuta e comunica l'esito."
          ctaLabel="Inizia Quiz"
          onClick={onOpenAssessment}
          testId="agenti-panel-assessment"
        />
        <AgentiPanelCard
          icon={Calendar}
          title="Calendario Eventi"
          subtitle={nextEvent ? "Prossimo evento" : "Briefing & operazioni"}
          desc={nextEvent
            ? `${nextEvent.title} · ${nextEvent.next_occurrence_at ? new Date(nextEvent.next_occurrence_at).toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}`
            : "Briefing settimanali, addestramenti, operazioni speciali e riunioni del Dipartimento."}
          ctaLabel="Apri Calendario"
          onClick={onOpenCalendar}
          testId="agenti-panel-calendar"
          badge={nextEvent ? "•" : null}
          badgeLabel={nextEvent ? "in arrivo" : null}
          badgeTone="gold"
        />
        {showExam && (
          <AgentiPanelCard
            highlight={examBadge === "disponibile"}
            icon={TrendingUp}
            title="Esame Promozione"
            subtitle={isLockedExam ? "Richiede Vice Sceriffo II" : "Bando promozione"}
            desc={isLockedExam
              ? "Visualizza il bando di promozione a Sergente. Per sostenere l'esame devi prima raggiungere il grado di Vice Sceriffo II."
              : "Sostieni l'esame teorico per la promozione al grado successivo. Il bando è gestito dal Comando e la classifica dalle HR."}
            ctaLabel={isLockedExam ? "Visualizza Bando" : "Apri Esame"}
            onClick={onOpenExam}
            testId="agenti-panel-exam"
            {...examBadgeProps}
          />
        )}
        <AgentiPanelCard
          icon={Scale}
          title="Mandati Procura"
          subtitle="Richieste al magistrato"
          desc="Compila una richiesta di mandato (perquisizione, arresto, sequestro). Al salvataggio il bot apre automaticamente un forum in Discord e tagga la Procura."
          ctaLabel="Apri Ufficio Rapporti"
          onClick={onOpenMandati}
          testId="agenti-panel-mandati"
        />
        <AgentiPanelCard
          icon={MessageSquareQuote}
          title="Feedback Operatori"
          subtitle="Segnala tutto e qualsiasi cosa"
          desc="Comunica al Comando problemi, idee, suggerimenti, bug del portale, rilievi operativi o su Discord. Puoi inviare anche in anonimo — ogni feedback viene letto."
          ctaLabel="Invia Feedback"
          onClick={onOpenFeedback}
          testId="agenti-panel-feedback"
        />
      </div>
    </div>
  );
}

function AgentiPanelCard({ icon: Icon, title, subtitle, desc, ctaLabel, onClick, testId, highlight, badge, badgeLabel, badgeTone }) {
  const baseCls = "relative overflow-hidden rounded-2xl border p-6 flex flex-col group transition-all hover:-translate-y-0.5 hover:shadow-2xl";
  const cls = highlight
    ? `${baseCls} border-sheriff-gold/60 bg-gradient-to-br from-sheriff-gold/20 via-[#143830] to-[#0F2620] hover:border-sheriff-gold shadow-lg shadow-sheriff-gold/10`
    : `${baseCls} border-sheriff-gold/25 bg-gradient-to-br from-[#143830] to-[#0F2620] hover:border-sheriff-gold/60`;
  let badgeCls;
  if (badgeTone === "gold") badgeCls = "bg-sheriff-gold/20 text-sheriff-gold border-sheriff-gold/50";
  else if (badgeTone === "amber") badgeCls = "bg-amber-500/15 text-amber-300 border-amber-500/40";
  else badgeCls = "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  return (
    <div data-testid={testId} className={cls}>
      {highlight && (
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-sheriff-gold/15 blur-3xl pointer-events-none" />
      )}
      <div className="relative flex items-start gap-3 mb-3">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 border ${highlight ? "border-sheriff-gold/60 bg-sheriff-gold/20" : "border-sheriff-gold/30 bg-sheriff-gold/10"}`}>
          <Icon className="h-6 w-6 text-sheriff-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="uppercase-label text-sheriff-gold/80 text-[10px] mb-0.5">{subtitle}</div>
          <h3 className="font-heading font-black text-2xl text-[#F5E6B8] tracking-tighter">{title}</h3>
        </div>
        {badge != null && (
          <span className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.12em] font-bold ${badgeCls}`} data-testid={`${testId}-badge`}>
            <span className="font-mono text-sm">{badge}</span>
            <span>{badgeLabel}</span>
          </span>
        )}
      </div>
      <p className="text-sm text-[#D4C480] mb-5 leading-relaxed relative">{desc}</p>
      <Button
        onClick={onClick}
        data-testid={`${testId}-cta`}
        className={`mt-auto rounded-full uppercase-label font-bold ${
          highlight
            ? "bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18]"
            : "bg-sheriff-gold/15 hover:bg-sheriff-gold/25 text-sheriff-gold border border-sheriff-gold/40"
        }`}
      >
        {ctaLabel} <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

function AgentiCalendarView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null); // Date | null

  useEffect(() => {
    api.get("/events", { params: { upcoming: false } })
      .then(({ data }) => setEvents(data))
      .catch(() => toast.error("Errore caricamento eventi"))
      .finally(() => setLoading(false));
  }, []);

  // Calcola tutte le occorrenze nel mese visualizzato
  const occurrencesByDay = useMemo(() => {
    const map = new Map(); // key = YYYY-MM-DD → [{event, date}]
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);
    (events || []).forEach((ev) => {
      if (!ev.is_active) return;
      if (ev.recurrence_type === "weekly") {
        const wd = ev.weekday; // 0 = Mon
        const [hh, mm] = (ev.start_time || "00:00").split(":").map(Number);
        // Trova il primo giorno del mese che è il weekday richiesto
        // JS getDay: 0=Sun, 1=Mon, ... → convertiamo: jsDay = (wd + 1) % 7
        const jsTargetDay = (wd + 1) % 7;
        const d = new Date(monthStart);
        while (d.getDay() !== jsTargetDay) d.setDate(d.getDate() + 1);
        while (d <= monthEnd) {
          const occ = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hh, mm);
          const key = `${occ.getFullYear()}-${String(occ.getMonth() + 1).padStart(2, "0")}-${String(occ.getDate()).padStart(2, "0")}`;
          if (!map.has(key)) map.set(key, []);
          map.get(key).push({ event: ev, date: occ });
          d.setDate(d.getDate() + 7);
        }
      } else if (ev.start_at) {
        const occ = new Date(ev.start_at);
        if (occ >= monthStart && occ <= monthEnd) {
          const key = `${occ.getFullYear()}-${String(occ.getMonth() + 1).padStart(2, "0")}-${String(occ.getDate()).padStart(2, "0")}`;
          if (!map.has(key)) map.set(key, []);
          map.get(key).push({ event: ev, date: occ });
        }
      }
    });
    return map;
  }, [events, cursor]);

  // Genera la matrice del mese (settimane × 7 giorni, lunedì first)
  const weeks = useMemo(() => {
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    // Offset: quanti giorni prima del 1° per arrivare al lunedì precedente
    // JS getDay 0=Dom, 1=Lun → vogliamo 1=Lun come 0
    const startOffset = (monthStart.getDay() + 6) % 7;
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - startOffset);
    const totalCells = Math.ceil((startOffset + monthEnd.getDate()) / 7) * 7;
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      cells.push(d);
    }
    // Raggruppa per settimana
    const out = [];
    for (let i = 0; i < cells.length; i += 7) out.push(cells.slice(i, i + 7));
    return out;
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const today = new Date();
  const isToday = (d) => d.toDateString() === today.toDateString();
  const isCurrentMonth = (d) => d.getMonth() === cursor.getMonth();
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    const d = new Date();
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>;
  }

  const selectedDayOccurrences = selectedDay ? (occurrencesByDay.get(dayKey(selectedDay)) || []) : [];

  return (
    <div className="space-y-4" data-testid="agenti-calendar-view">
      {/* Header navigazione */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button onClick={goPrev} variant="outline" size="sm" data-testid="cal-prev" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-heading font-bold text-2xl text-[#F5E6B8] capitalize min-w-[200px] text-center">{monthLabel}</h3>
          <Button onClick={goNext} variant="outline" size="sm" data-testid="cal-next" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button onClick={goToday} variant="outline" size="sm" data-testid="cal-today" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label text-[10px]">
          Oggi
        </Button>
      </div>

      {/* Griglia calendario */}
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl overflow-hidden">
        {/* Header giorni */}
        <div className="grid grid-cols-7 border-b border-sheriff-gold/15">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
            <div key={d} className="p-3 text-center uppercase-label text-sheriff-gold/70 text-[10px] font-bold border-r border-sheriff-gold/10 last:border-r-0">{d}</div>
          ))}
        </div>
        {/* Settimane */}
        <div>
          {weeks.map((week, wi) => (
            <div key={`week-${wi}-${dayKey(week[0])}`} className="grid grid-cols-7 border-b border-sheriff-gold/10 last:border-b-0">
              {week.map((d) => {
                const occs = occurrencesByDay.get(dayKey(d)) || [];
                const today_ = isToday(d);
                const inMonth = isCurrentMonth(d);
                const clickable = occs.length > 0;
                let cellCls = "min-h-[90px] p-2 border-r border-sheriff-gold/10 last:border-r-0 transition-all relative ";
                if (!inMonth) cellCls += "bg-[#0A1F18]/50 text-[#8B7B49]/40 ";
                else cellCls += "text-[#E8D89A] ";
                if (today_) cellCls += "ring-1 ring-inset ring-sheriff-gold/60 bg-sheriff-gold/5 ";
                if (clickable) cellCls += "hover:bg-sheriff-gold/10 cursor-pointer ";
                return (
                  <button
                    key={dayKey(d)}
                    type="button"
                    onClick={() => clickable && setSelectedDay(d)}
                    data-testid={`cal-day-${dayKey(d)}`}
                    disabled={!clickable}
                    className={`${cellCls} text-left w-full`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className={`font-mono-codes text-sm font-bold ${today_ ? "text-sheriff-gold" : ""}`}>{d.getDate()}</span>
                      {occs.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full bg-sheriff-gold/20 border border-sheriff-gold/40 text-[9px] font-mono text-sheriff-gold font-bold">
                          {occs.length}
                        </span>
                      )}
                    </div>
                    {occs.slice(0, 2).map((o, oi) => (
                      <div key={`${o.event.id || o.event.title}-${oi}`} className="text-[10px] leading-tight mb-1 truncate px-1.5 py-0.5 rounded bg-sheriff-gold/15 border-l-2 border-sheriff-gold text-[#F5E6B8]">
                        <span className="font-mono">{o.date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span> {o.event.title}
                      </div>
                    ))}
                    {occs.length > 2 && (
                      <div className="text-[9px] text-sheriff-gold/70 italic">+ altri {occs.length - 2}</div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-4 text-[10px] text-[#8B7B49] uppercase tracking-[0.15em]">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded ring-1 ring-sheriff-gold/60 bg-sheriff-gold/5"></span> Oggi
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-sheriff-gold/15 border-l-2 border-sheriff-gold"></span> Evento
        </div>
      </div>

      {/* Dialog dettagli giorno */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-md bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl" data-testid="cal-day-dialog">
          <DialogHeader>
            <div className="uppercase-label text-sheriff-gold mb-1">
              {selectedDay && selectedDay.toLocaleDateString("it-IT", { weekday: "long" })}
            </div>
            <DialogTitle className="font-heading font-black text-2xl text-[#F5E6B8] capitalize">
              {selectedDay && selectedDay.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {selectedDayOccurrences.map((o, i) => (
              <div key={`${o.event.id || o.event.title}-${i}`} className="bg-[#0F2620] border border-sheriff-gold/30 rounded-xl p-4" data-testid={`day-event-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 px-2.5 py-1.5 rounded-lg bg-sheriff-gold/15 border border-sheriff-gold/40">
                    <div className="font-mono-codes text-xs text-sheriff-gold font-bold">
                      {o.date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-heading font-bold text-[#F5E6B8] truncate">{o.event.title}</h4>
                      {o.event.recurrence_type === "weekly" && (
                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-[8px] uppercase tracking-[0.15em] font-bold text-emerald-300 shrink-0">Settimanale</span>
                      )}
                    </div>
                    {o.event.description && (
                      <p className="text-xs text-[#D4C480] whitespace-pre-wrap mb-2">{o.event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.12em] text-[#8B7B49]">
                      {o.event.duration_minutes && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-sheriff-gold/60" /> {o.event.duration_minutes} min
                        </div>
                      )}
                      {o.event.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-sheriff-gold/60" /> {o.event.location}
                        </div>
                      )}
                    </div>
                    {o.event.id && <EventRsvpBar eventId={o.event.id} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LockedPromotionExam({ userRole, requiredRole, nextRole }) {
  return (
    <div className="space-y-6" data-testid="promotion-exam-locked">
      <div className="relative bg-gradient-to-br from-[#0F2620] to-[#0A1F18] border border-sheriff-gold/15 rounded-2xl p-6 sm:p-8 overflow-hidden">
        {/* Overlay diagonale per effetto "bloccato" */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "repeating-linear-gradient(45deg, #F5E6B8 0px, #F5E6B8 1px, transparent 1px, transparent 14px)"
        }} />

        <div className="relative">
          <div className="uppercase-label text-sheriff-gold/50 mb-2 flex items-center gap-2">
            <Lock className="h-4 w-4" /> Esame di Promozione · Bloccato
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8]/40 mb-2">
            Promozione a {nextRole}
          </h2>
          <p className="text-[#D4C480]/60 mb-5 max-w-2xl">
            Il tuo grado attuale è <span className="text-sheriff-gold/70 font-semibold">{userRole}</span>. Per
            sostenere l&apos;esame di promozione a <span className="text-sheriff-gold/70 font-semibold">{nextRole}</span> devi
            prima raggiungere il grado di <span className="text-sheriff-gold font-bold">{requiredRole}</span>.
          </p>

          <div data-testid="locked-rank-banner" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-5 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-300 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-heading font-bold text-amber-300 mb-1">Grado insufficiente</div>
              <p className="text-sm text-[#D4C480]">
                Continua a svolgere il servizio e ottieni le promozioni interne fino a raggiungere
                il grado <span className="text-sheriff-gold font-semibold">{requiredRole}</span>.
                Solo allora questo esame sarà sbloccato.
              </p>
            </div>
          </div>

          <ul className="text-sm text-[#E8D89A]/40 space-y-1 mb-6 list-disc list-inside">
            <li className="text-amber-300/60"><span className="font-bold">Prima della prova scritta</span> sosterrai anche un <span className="font-bold">colloquio orale</span> con il Comando</li>
            <li>Tempo limite: <span className="font-bold">15 minuti</span></li>
            <li>Soglia di superamento: <span className="font-bold">configurata dal Comando</span></li>
            <li>Cooldown tra tentativi: <span className="font-bold">5 giorni</span></li>
            <li>I risultati saranno valutati dalle <span className="font-bold">HR</span> sulla base della classifica</li>
          </ul>

          <Button
            disabled
            data-testid="locked-exam-btn"
            className="bg-sheriff-gold/20 text-[#F5E6B8]/40 font-bold uppercase tracking-[0.12em] rounded-full px-6 cursor-not-allowed border border-sheriff-gold/20"
          >
            <Lock className="h-4 w-4 mr-2" /> Esame Bloccato
          </Button>
        </div>
      </div>
    </div>
  );
}

function PromotionExamLauncher({ userRole }) {
  const [started, setStarted] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mapping ruolo → tier d'esame
  const tierByRole = {
    "Vice Sceriffo II": "sergente",
    "Sergente": "tenente",
    "Tenente": "capitano",
  };
  const tierLabelsByRole = {
    "Vice Sceriffo II": "Sergente",
    "Sergente": "Tenente",
    "Tenente": "Capitano",
  };
  const nextRole = tierLabelsByRole[userRole] || "Grado Successivo";
  const tier = tierByRole[userRole];

  useEffect(() => {
    if (!tier) return;
    let cancelled = false;
    (async () => {
      try {
        // /quiz/questions ritorna anche lo stato sessione
        const { data } = await api.get("/quiz/questions");
        if (!cancelled) setSessionInfo(data);
      } catch {
        if (!cancelled) toast.error("Impossibile verificare lo stato del bando");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tier]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/quiz/questions");
      setSessionInfo(data);
    } catch (e) {
      // silent
    }
  }, []);

  if (started) return <ExamPanel onExit={() => { setStarted(false); setLoading(true); }} />;

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>;

  const isOpen = sessionInfo?.session_open;
  const inCooldown = sessionInfo?.block_reason === "cooldown";
  const authBlocked = sessionInfo?.block_reason === "auth_required";
  const stepsPending = sessionInfo?.block_reason === "hr_steps_pending";
  const isApproved = sessionInfo?.auth_status === "approved";
  const nextAttemptAt = sessionInfo?.next_attempt_at;
  const cooldownDays = sessionInfo?.cooldown_days || 5;
  const slots = sessionInfo?.session?.slots || 0;
  const promoted = sessionInfo?.session?.promoted_count || 0;
  const remaining = Math.max(slots - promoted, 0);

  return (
    <div className="space-y-6" data-testid="promotion-exam-launcher">
      <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/30 rounded-2xl p-6 sm:p-8">
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Esame di Promozione
        </div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8] mb-2">
          Promozione a {nextRole}
        </h2>
        <p className="text-[#D4C480] mb-5 max-w-2xl">
          Il tuo grado attuale (<span className="text-sheriff-gold font-semibold">{userRole}</span>) ti permette di sostenere
          l&apos;esame teorico per la promozione a <span className="text-sheriff-gold font-semibold">{nextRole}</span>.
        </p>

        {/* Stato bando + autorizzazione HR */}
        {!isOpen && (
          <div data-testid="session-closed-banner" className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 mb-5">
            <div className="font-heading font-bold text-rose-300 mb-1">Nessun bando attivo</div>
            <p className="text-sm text-[#D4C480]">
              Il Comando non ha attualmente un bando aperto per la promozione a <span className="text-sheriff-gold font-semibold">{nextRole}</span>.
              Torna a controllare quando verrà aperto.
            </p>
          </div>
        )}

        {isOpen && authBlocked && (
          <div className="mb-5">
            <ExamAuthRequestPanel
              sessionInfo={sessionInfo}
              tierLabel={sessionInfo?.exam_tier_label || nextRole}
              nextRole={nextRole}
              onRefresh={refresh}
            />
          </div>
        )}

        {isOpen && isApproved && stepsPending && (
          <div className="mb-5">
            <ExamAuthRequestPanel
              sessionInfo={sessionInfo}
              tierLabel={sessionInfo?.exam_tier_label || nextRole}
              nextRole={nextRole}
              onRefresh={refresh}
            />
          </div>
        )}

        {isOpen && isApproved && inCooldown && (
          <div data-testid="cooldown-banner" className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-5">
            <div className="font-heading font-bold text-amber-300 mb-1">Cooldown attivo · {cooldownDays} giorni</div>
            <p className="text-sm text-[#D4C480]">
              Hai sostenuto l&apos;esame di recente. Potrai ritentare a partire dal{" "}
              <span className="text-sheriff-gold font-semibold">
                {nextAttemptAt ? new Date(nextAttemptAt).toLocaleString("it-IT") : "—"}
              </span>.
            </p>
          </div>
        )}

        {isOpen && isApproved && !inCooldown && !stepsPending && (
          <div data-testid="session-open-banner" className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4 mb-5">
            <div className="font-heading font-bold text-emerald-300 mb-1 flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" /> Autorizzazione HR ottenuta — Puoi iniziare l&apos;esame
            </div>
            <p className="text-sm text-[#D4C480]">
              Posti rimanenti: <span className="text-sheriff-gold font-black text-base">{remaining}</span> di {slots}.
              Verranno promossi i candidati con il <span className="text-sheriff-gold font-semibold">punteggio più alto</span>.
              Se non superi l&apos;esame puoi ritentare dopo <span className="text-sheriff-gold font-semibold">{cooldownDays} giorni</span>.
            </p>
          </div>
        )}

        <ul className="text-sm text-[#E8D89A] space-y-1 mb-6 list-disc list-inside">
          <li className="text-amber-300"><span className="font-bold">Prima dell&apos;esame</span> serve l&apos;<span className="font-bold">autorizzazione delle Risorse Umane</span> sulla tua richiesta</li>
          <li>Tempo limite: <span className="font-bold text-sheriff-gold">15 minuti</span></li>
          <li>Soglia di superamento: <span className="font-bold text-sheriff-gold">{sessionInfo?.pass_threshold || "—"}%</span></li>
          <li>Cooldown tra tentativi: <span className="font-bold text-sheriff-gold">{cooldownDays} giorni</span></li>
          <li>Il bando si chiude automaticamente al raggiungimento dei posti disponibili</li>
          <li>I risultati saranno valutati dalle <span className="font-bold text-sheriff-gold">HR</span> sulla base della classifica</li>
        </ul>
        <Button
          onClick={() => setStarted(true)}
          disabled={!isOpen || !isApproved || inCooldown || stepsPending}
          data-testid="start-promotion-exam"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-[0.12em] rounded-full px-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ClipboardCheck className="h-4 w-4 mr-2" /> Inizia Esame
        </Button>
      </div>
    </div>
  );
}

export function PenalCodeBrowser() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/penal-code").then(({ data }) => setItems(data)).catch(() => toast.error("Errore caricamento codice penale")).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    items.forEach((p) => { if (!m.has(p.category)) m.set(p.category, []); m.get(p.category).push(p); });
    return m;
  }, [items]);

  const orderedCats = useMemo(() => Array.from(grouped.keys()).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a); const bi = CATEGORY_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  }), [grouped]);

  const searchResults = useMemo(() => {
    if (!q.trim()) return null;
    return items.filter((p) => [p.code, p.title, p.category, p.description].some((s) => (s || "").toLowerCase().includes(q.toLowerCase())));
  }, [q, items]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>;

  return (
    <div className="space-y-5">
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-3 flex items-center gap-3">
        <Search className="h-4 w-4 text-sheriff-gold/60 ml-2" />
        <Input data-testid="agent-penal-search" placeholder="Cerca per codice, reato o descrizione…" value={q} onChange={(e) => setQ(e.target.value)} className="border-0 focus-visible:ring-0 bg-transparent px-0" />
      </div>

      {searchResults ? (
        searchResults.length === 0 ? (
          <div className="text-center text-[#B8A269] py-12">Nessun risultato per &quot;{q}&quot;</div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {searchResults.map((p) => <AgentArticleCard key={p.code + p.title} item={p} onPick={setActive} showCat />)}
          </div>
        )
      ) : cat === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agent-cat-grid">
          {orderedCats.map((c, idx) => {
            const arr = grouped.get(c);
            return (
              <button key={c} onClick={() => setCat(c)} className="group text-left bg-gradient-to-br from-[#143830] to-[#0F2620] hover:from-[#1B4D3E] hover:to-[#143830] border border-sheriff-gold/20 hover:border-sheriff-gold/60 rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60">Capitolo {String(idx + 1).padStart(2, "0")}</div>
                  <ChevronRight className="h-4 w-4 text-sheriff-gold/40 group-hover:text-sheriff-gold group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="font-heading font-bold text-base text-[#F5E6B8] group-hover:text-sheriff-gold transition-colors leading-tight mb-2">{c}</h3>
                <div className="text-xs font-mono text-sheriff-gold">{arr.length} articoli</div>
              </button>
            );
          })}
        </div>
      ) : (
        <CatDetail category={cat} items={grouped.get(cat) || []} onBack={() => setCat(null)} onPick={setActive} />
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="agent-penal-detail-dialog">
          {active && <ArticleDetail item={active} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CatDetail({ category, items, onBack, onPick }) {
  const subgroups = useMemo(() => {
    const m = new Map();
    items.forEach((p) => { if (!m.has(p.severity)) m.set(p.severity, []); m.get(p.severity).push(p); });
    return Array.from(m.entries()).sort(([a], [b]) => (SEVERITY[b]?.order || 0) - (SEVERITY[a]?.order || 0));
  }, [items]);

  return (
    <div data-testid={`agent-cat-detail-${category}`}>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sheriff-gold hover:text-sheriff-gold-dark uppercase-label mb-5 transition-colors">
        <ChevronLeft className="h-4 w-4" /> Torna ai Capitoli
      </button>
      <div className="mb-6">
        <h3 className="font-heading font-black text-2xl text-[#F5E6B8] tracking-tighter">{category}</h3>
        <p className="text-[#B8A269] mt-1 font-mono text-xs">{items.length} articoli</p>
      </div>
      <div className="space-y-8">
        {subgroups.map(([sev, arr]) => {
          const s = SEVERITY[sev] || SEVERITY.INFRAZIONE;
          return (
            <div key={sev}>
              <div className="flex items-center gap-3 mb-3">
                <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                <h4 className="font-heading font-bold text-base text-[#F5E6B8]">{sev}</h4>
                <span className="text-xs font-mono text-[#8B7B49]">· {arr.length}</span>
                <div className="flex-1 h-px bg-sheriff-gold/15" />
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {arr.map((p) => <AgentArticleCard key={p.code + p.title} item={p} onPick={onPick} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgentArticleCard({ item, onPick, showCat }) {
  const s = SEVERITY[item.severity] || SEVERITY.INFRAZIONE;
  return (
    <button onClick={() => onPick(item)} data-testid={`agent-art-${item.code}`} className="group text-left bg-[#143830] hover:bg-[#1B4D3E] border border-sheriff-gold/20 hover:border-sheriff-gold/60 rounded-2xl p-4 transition-all hover:-translate-y-0.5">
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="font-mono font-bold text-sheriff-gold text-sm">{item.code}</span>
        <ChevronRight className="h-3.5 w-3.5 text-sheriff-gold/40 group-hover:text-sheriff-gold transition-all" />
      </div>
      <h5 className="font-heading font-bold text-sm text-[#F5E6B8] group-hover:text-sheriff-gold transition-colors leading-snug mb-2 line-clamp-2">{item.title}</h5>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] font-bold rounded-full border ${s.chip}`}>
          <span className={`h-1 w-1 rounded-full ${s.dot}`} />{item.severity}
        </span>
        {item.process && <span className="text-[9px] uppercase tracking-[0.1em] font-bold rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/40 px-2 py-0.5">⚖</span>}
        {item.bail && <span data-testid={`agent-penal-bail-badge-${item.code}`} className="text-[9px] uppercase tracking-[0.1em] font-bold rounded-full bg-emerald-500/15 text-emerald-300 border-emerald-500/40 border px-2 py-0.5">Cauzione</span>}
      </div>
      {showCat && <div className="text-[10px] text-[#8B7B49] uppercase tracking-[0.1em] mb-1">{item.category}</div>}
      <div className="flex items-center justify-between text-xs font-mono pt-2 border-t border-sheriff-gold/10">
        <span className="text-[#D4C480]">{item.jail}</span>
        <span className="text-sheriff-gold font-bold">{item.fine}</span>
      </div>
    </button>
  );
}

function ArticleDetail({ item }) {
  const s = SEVERITY[item.severity] || SEVERITY.INFRAZIONE;
  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full border ${s.chip}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{item.severity}
          </span>
          {item.process && <span className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full border bg-purple-500/15 text-purple-300 border-purple-500/40"><Gavel className="h-2.5 w-2.5" /> Processo</span>}
          {item.bail && <span data-testid="agent-penal-detail-bail-badge" className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Cauzione</span>}
          <span className="inline-flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-bold rounded-full bg-sheriff-gold/10 text-sheriff-gold/80"><Folder className="h-2.5 w-2.5" /> {item.category}</span>
        </div>
        <DialogTitle className="font-heading font-black text-2xl sm:text-3xl text-[#F5E6B8] leading-tight pr-8 text-left">
          <span className="font-mono text-sheriff-gold mr-3">{item.code}</span>{item.title}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-5 pt-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold mb-2">Descrizione</div>
          <p className="text-[#E8D89A] leading-relaxed whitespace-pre-wrap text-sm">{item.description}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#0A1F18] border border-sheriff-gold/20 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/70 flex items-center gap-1.5 mb-2"><Lock className="h-3 w-3" /> Detenzione</div>
            <div className="font-mono text-[#F5E6B8] font-bold text-lg">{item.jail}</div>
          </div>
          <div className="bg-[#0A1F18] border border-sheriff-gold/20 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/70 flex items-center gap-1.5 mb-2"><DollarSign className="h-3 w-3" /> Multa</div>
            <div className="font-mono text-sheriff-gold font-bold text-lg">{item.fine}</div>
          </div>
        </div>
        <div className="text-xs text-[#8B7B49] italic">Consultazione in sola lettura. Modifiche riservate al Comando.</div>
      </div>
    </>
  );
}

export function RadioCodes() {
  const [q, setQ] = useState("");
  const fr = RADIO_CODES.filter((r) => [r.code, r.desc].some((s) => s.toLowerCase().includes(q.toLowerCase())));
  const isEmergency = (c) => /^(11-99|10-0|998|10-101|10-50)$/.test(c);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-sheriff-gold/20 bg-[#0F2620]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sheriff-gold mb-0.5">Protocollo Operativo</div>
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><Radio className="h-4 w-4" /> Codici Emergenza e Operativi</h3>
        </div>
        <div className="p-4 flex items-center gap-3 border-b border-sheriff-gold/15">
          <Search className="h-4 w-4 text-sheriff-gold/60" />
          <Input data-testid="radio-search" placeholder="Cerca codice…" value={q} onChange={(e) => setQ(e.target.value)} className="border-0 focus-visible:ring-0 px-0 bg-transparent" />
        </div>
        <Table>
          <TableBody>
            {fr.map((r) => {
              const emer = isEmergency(r.code);
              return (
                <TableRow key={r.code} className={emer ? "bg-rose-500/10 hover:bg-rose-500/15" : "hover:bg-sheriff-gold/5"}>
                  <TableCell className={`font-mono-codes font-bold w-28 ${emer ? "text-rose-300" : "text-sheriff-gold"}`}>
                    {r.code}
                  </TableCell>
                  <TableCell className={emer ? "text-rose-200 font-semibold" : "text-[#E8D89A]"}>
                    {r.desc}
                  </TableCell>
                </TableRow>
              );
            })}
            {fr.length === 0 && (
              <TableRow><TableCell colSpan={2} className="text-center text-[#B8A269] py-6">Nessun codice trovato</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl h-fit overflow-hidden">
        <div className="p-4 border-b border-sheriff-gold/20 bg-[#06140F]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sheriff-gold mb-0.5">Stato Veicolo</div>
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><Radio className="h-4 w-4 text-sheriff-gold" /> Codici Operativi Principali</h3>
        </div>
        <Table>
          <TableBody>
            {STATUS_CODES.map((s) => (
              <TableRow key={s.code} className="hover:bg-sheriff-gold/5">
                <TableCell className="font-mono-codes font-bold text-sheriff-gold-dark w-32">{s.code}</TableCell>
                <TableCell className="text-[#E8D89A]">{s.desc}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function EventRsvpBar({ eventId }) {
  const [status, setStatus] = useState(null); // "yes" | "no" | "maybe" | null
  const [counts, setCounts] = useState({ yes: 0, no: 0, maybe: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/events/${eventId}/my-rsvp`).then(({ data }) => {
      if (cancelled) return;
      setStatus(data.status || null);
      setCounts(data.counts || { yes: 0, no: 0, maybe: 0 });
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [eventId]);

  const set = async (newStatus) => {
    if (saving) return;
    setSaving(true);
    const prev = status;
    setStatus(newStatus); // optimistic
    try {
      const { data } = await api.post(`/events/${eventId}/rsvp`, { status: newStatus });
      setCounts(data.counts || counts);
      toast.success("Partecipazione aggiornata");
    } catch (e) {
      setStatus(prev);
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally { setSaving(false); }
  };

  if (loading) return null;

  const btn = (val, label, activeCls) => (
    <button
      onClick={() => set(val)}
      disabled={saving}
      className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border transition-all ${
        status === val ? activeCls : "border-sheriff-gold/25 text-sheriff-gold/60 hover:border-sheriff-gold/50 hover:text-sheriff-gold"
      }`}
      data-testid={`rsvp-${val}-${eventId}`}
    >
      {label} <span className="opacity-60">· {counts[val] || 0}</span>
    </button>
  );

  return (
    <div className="mt-3 pt-3 border-t border-sheriff-gold/15 flex items-center gap-2 flex-wrap">
      <span className="dossier-mono text-[10px] uppercase tracking-[0.2em] text-sheriff-gold/50 mr-1">Ci sarai?</span>
      {btn("yes", "Sì", "bg-emerald-500/15 border-emerald-400 text-emerald-300")}
      {btn("maybe", "Forse", "bg-amber-500/15 border-amber-400 text-amber-300")}
      {btn("no", "No", "bg-rose-500/15 border-rose-400 text-rose-300")}
    </div>
  );
}

